import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { toPercent } from 'pano-utils';
import limitNum from '@utils/limitNum';
import { roundNum } from '@utils/roundNum';
import ImgLeft from './img-left.png';
import ImgRight from './img-right.png';
import ImgCenter from './img-center.png';
import SliderInput from './SliderInput';

export type ViewSliderKey = 'left' | 'right' | 'center';

interface Props {
  min: number;
  max: number;
  step?: number;
  leftValue: number;
  rightValue: number;
  centerValue?: number;
  className?: string;
  leftTitle: string;
  rightTitle: string;
  centerTitle?: string;
  digits?: number;
  centerReadonly?: boolean;
  onChange: (params: { key: ViewSliderKey; value: number }) => void;
}

const Bar = styled.div`
  position: relative;
  width: 100%;
  height: 27px;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 12px;
    background-color: #1a1a1a;
    height: 3px;
  }
`;
const Handler = styled.img.attrs({
  draggable: false,
})`
  position: absolute;
  cursor: pointer;
  width: 9px;
  height: 15px;
`;
const Left = styled(Handler).attrs({
  src: ImgLeft,
})`
  margin-left: -9px;
  bottom: 0;
`;
const Right = styled(Handler).attrs({
  src: ImgRight,
})`
  bottom: 0;
`;
const Center = styled(Handler).attrs({
  src: ImgCenter,
})`
  margin-left: -4px;
`;
const ValueBar = styled.div`
  position: absolute;
  top: 12px;
  height: 3px;
  background-color: #286efa;
`;

function ViewSlider({
  min,
  max,
  step = 1,
  leftValue,
  rightValue,
  centerValue,
  className,
  leftTitle,
  rightTitle,
  centerTitle,
  digits = 1,
  centerReadonly = false,
  onChange,
}: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const prevLeft = useRef(leftValue);
  const prevCenter = useRef(centerValue);
  const prevRight = useRef(rightValue);
  const range = max - min;
  const getLPos = (val: number) => toPercent((val - min) / range);
  const getRPos = (val: number) => toPercent(1 - (val - min) / range);

  const ratio = useMemo(() => {
    const decimal = String(step).split('.')[1];
    if (!decimal) return 1;
    return 10 ** decimal.length;
  }, [step]);

  const getVal = (e: MouseEvent) => {
    if (!divRef.current) return 0;
    const { left, right, width } = divRef.current.getBoundingClientRect();
    const clientX = limitNum({ min: left, max: right, value: e.clientX });
    const roundedVal = Math.round((((clientX - left) / width) * range) / step);
    return Math.round((roundedVal * step + min) * ratio) / ratio;
  };

  const handleLMouseDown = (key: ViewSliderKey) => () => {
    const handleMouseMove = (e: MouseEvent) => {
      if (key === 'left') {
        const value = limitNum({
          min,
          max: !centerReadonly && centerValue ? centerValue : rightValue,
          value: getVal(e),
        });
        if (prevLeft.current !== value) {
          onChange({ key, value });
        }
        prevLeft.current = value;
      } else if (key === 'center') {
        const value = limitNum({
          min: leftValue,
          max: rightValue,
          value: getVal(e),
        });
        if (prevCenter.current !== value) {
          onChange({ key, value });
        }
        prevCenter.current = value;
      } else if (key === 'right') {
        const value = limitNum({
          min: !centerReadonly && centerValue ? centerValue : leftValue,
          max,
          value: getVal(e),
        });
        if (prevRight.current !== value) {
          onChange({ key, value });
        }
        prevRight.current = value;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mouseup', handleMouseUp, false);
      document.removeEventListener('mousemove', handleMouseMove, false);
    };
    document.addEventListener('mouseup', handleMouseUp, false);
    document.addEventListener('mousemove', handleMouseMove, false);
  };

  return (
    <div className={className}>
      <div className="px-5">
        <Bar ref={divRef}>
          <ValueBar
            style={{ left: getLPos(leftValue), right: getRPos(rightValue) }}
          />
          <Left
            style={{ left: getLPos(leftValue) }}
            onMouseDown={handleLMouseDown('left')}
          />
          {centerValue !== undefined && (
            <Center
              style={{ left: getLPos(centerValue) }}
              onMouseDown={
                centerReadonly ? undefined : handleLMouseDown('center')
              }
            />
          )}
          <Right
            style={{ left: getLPos(rightValue) }}
            onMouseDown={handleLMouseDown('right')}
          />
        </Bar>
      </div>
      <div className="mt-20 flex items-center justify-between text-sm">
        <div className="flex flex-col items-center">
          <SliderInput
            min={min}
            max={centerReadonly ? rightValue : centerValue ?? rightValue}
            value={leftValue}
            digits={digits}
            onChange={(value) => onChange({ key: 'left', value })}
          />
          <div className="mt-4">{leftTitle}</div>
        </div>
        {centerValue !== undefined && (
          <div className="flex flex-col items-center">
            {centerReadonly ? (
              <div className="flex h-20 w-40 items-center justify-center text-sm">
                {roundNum(digits, centerValue)}
              </div>
            ) : (
              <SliderInput
                min={leftValue}
                max={rightValue}
                value={centerValue}
                digits={digits}
                onChange={(value) => onChange({ key: 'center', value })}
              />
            )}
            <div className="mt-4">{centerTitle}</div>
          </div>
        )}
        <div className="flex flex-col items-center">
          <SliderInput
            min={centerReadonly ? leftValue : centerValue ?? leftValue}
            max={max}
            value={rightValue}
            digits={digits}
            onChange={(value) => onChange({ key: 'right', value })}
          />
          <div className="mt-4">{rightTitle}</div>
        </div>
      </div>
    </div>
  );
}

export default ViewSlider;
